// Renders all fields of a Zod schema automatically by introspecting each field's type and .meta().
// The meta.field value determines which input component is used; base type is the fallback.
// TODO: fields ending in '_country_code' are excluded because AddressFieldGroup owns them —
// find a cleaner way to express field ownership in the schema instead of relying on naming conventions.
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { MessagingAppEnum } from '@mr-website/schemas';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';
import { z } from 'zod';
import { AddressFieldGroup } from './fields/AddressFieldGroup';
import { DatePicker } from './fields/DatePicker';
import { SpouseFieldGroup } from './fields/SpouseFieldGroup';
import { PhoneField } from './fields/PhoneField';
import { PropertiesField } from './fields/PropertiesField';
import { SelectField } from './fields/SelectField';
import { TextField } from './fields/TextField';
import { TypedMultiInput } from './fields/TypedMultiInput';

const schemaFormFieldsVariants = cva('[&_[data-slot=field-content]]:w-full', {
  variants: {
    variant: {
      inline:
        'grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-x-10 gap-y-2 items-baseline lg:[&_[data-slot=field-content]]:w-auto',
      stacked: 'flex flex-col gap-y-4 [&_[data-slot=field]]:w-full',
    },
  },
  defaultVariants: {
    variant: 'inline',
  },
});

export interface SchemaFormFieldsProps extends VariantProps<typeof schemaFormFieldsVariants> {
  form: {
    AppField: React.ComponentType<any>;
    AppForm: React.ComponentType<any>;
    setFieldValue: (field: any, value: any) => void;
    store: any;
  };
  editMode: boolean;
  isLoading: boolean;
  disabled?: boolean;
  zodSchema: z.ZodObject;
  className?: string;
}

export function SchemaFormFields({
  form,
  editMode,
  isLoading,
  disabled = false,
  zodSchema,
  variant,
  className,
}: SchemaFormFieldsProps) {
  const resolvedVariant = variant ?? ('inline' as const);

  const filterFieldName = (name: string) => {
    if (name.endsWith('_country_code')) return false;
    return true;
  };

  if (isLoading) {
    const fieldNames = (Object.keys(zodSchema.shape) as string[]).filter(filterFieldName);
    return (
      <div className={cn(schemaFormFieldsVariants({ variant }), className)}>
        {fieldNames.map((name) => (
          <Skeleton key={name} className="h-4 w-full" />
        ))}
      </div>
    );
  }

  const { AppField, AppForm } = form;

  const fieldNames = (Object.keys(zodSchema.shape) as string[]).filter(filterFieldName);

  return (
    <AppForm>
      <div className={cn(schemaFormFieldsVariants({ variant }), className)}>
        {fieldNames.map((name) => {
          const schemaField = zodSchema.shape[name];
          let base = schemaField;
          while (base?.def?.innerType) base = base.def.innerType;
          const meta = base.meta?.();

          if (meta?.field === 'address') {
            return (
              <AddressFieldGroup
                key={name}
                form={form}
                editMode={editMode}
                isLoading={isLoading}
                variant={resolvedVariant}
              />
            );
          }

          if (meta?.field === 'spouse') {
            return (
              <SpouseFieldGroup
                key={name}
                form={form}
                editMode={editMode}
                isLoading={isLoading}
                variant={resolvedVariant}
              />
            );
          }

          return (
            <AppField key={name} name={name}>
              {() => {
                const isRequired = schemaField.def?.type !== 'optional';
                const label =
                  (meta?.label as string) ??
                  name
                    .split('_')
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(' ');
                if (meta?.field === 'phone_number') {
                  return (
                    <PhoneField
                      label={label}
                      readOnly={!editMode}
                      loading={isLoading}
                      disabled={disabled}
                      required={isRequired}
                      variant={resolvedVariant}
                    />
                  );
                }
                if (meta?.field === 'select') {
                  if (meta.optionsApi) {
                    return (
                      <SelectField
                        label={label}
                        optionsApi={meta.optionsApi as string}
                        optionsApiCallback={
                          meta.optionsApiCallback as ((v: any) => string) | undefined
                        }
                        readOnly={!editMode}
                        loading={isLoading}
                        disabled={disabled}
                        required={isRequired}
                        variant={resolvedVariant}
                      />
                    );
                  }
                  const options =
                    meta.options ??
                    (base.type === 'enum' && base.def?.entries
                      ? Object.entries(base.def.entries as Record<string, string>).map(([, v]) => ({
                          value: v,
                          label: v,
                        }))
                      : []);
                  return (
                    <SelectField
                      label={label}
                      options={options}
                      readOnly={!editMode}
                      loading={isLoading}
                      disabled={disabled}
                      required={isRequired}
                      variant={resolvedVariant}
                    />
                  );
                }
                if (meta?.field === 'date') {
                  return (
                    <DatePicker
                      label={label}
                      readOnly={!editMode}
                      loading={isLoading}
                      disabled={disabled}
                      required={isRequired}
                      variant={resolvedVariant}
                    />
                  );
                }
                if (base.type === 'string') {
                  if (meta?.field === 'phone_number') {
                    return (
                      <PhoneField
                        label={label}
                        readOnly={!editMode}
                        loading={isLoading}
                        disabled={disabled}
                        required={isRequired}
                        variant={resolvedVariant}
                      />
                    );
                  }
                  return (
                    <TextField
                      label={label}
                      readOnly={!editMode}
                      loading={isLoading}
                      disabled={disabled}
                      autocomplete={meta?.autocomplete as string | undefined}
                      required={isRequired}
                      variant={resolvedVariant}
                    />
                  );
                }

                if (meta?.field === 'messaging_apps') {
                  return (
                    <TypedMultiInput
                      label={label}
                      readOnly={!editMode}
                      loading={isLoading}
                      disabled={disabled}
                      options={Object.values(MessagingAppEnum).map((value) => ({
                        value,
                        label: value,
                      }))}
                      variant={resolvedVariant}
                    />
                  );
                }

                if (meta?.field === 'properties') {
                  return (
                    <PropertiesField readOnly={!editMode} loading={isLoading} disabled={disabled} />
                  );
                }

                return null;
              }}
            </AppField>
          );
        })}
      </div>
    </AppForm>
  );
}
